import { CustomUseQueryOptions } from '../types';
import { hasNonEmptyProp } from '@/libs/common/utils';
import type { ApiInstance, MarketDetail, MarketDetailParams } from '@/libs/sdk/api';
import { MarketStatus } from '@/libs/sdk/api';
import { useSSEQuery } from '../hooks/use-sse-query';

export type UseMarketDetailOptions = CustomUseQueryOptions<MarketDetail> & {
  subscribe?: boolean;
};

// After resolution, override option prices to reflect payouts (winner=1, loser=0)
const applyResolvedPrices = (data: MarketDetail): MarketDetail => {
  if (data.marketStatus !== MarketStatus.closed || !data.resultOptionAddress) return data;
  const decimal = data.optionList[0]?.decimal ?? 6;
  const one = String(10 ** decimal); // "1000000" for 6 decimals
  return {
    ...data,
    optionList: data.optionList.map(opt => ({
      ...opt,
      price: opt.optionAddress === data.resultOptionAddress ? one : '0',
    })),
  };
};

// Shared query options so other consumers (e.g. the event page's per-market
// fan-out) hit the SAME cache entry as useMarketDetail — identical key AND the
// same resolved-price transform — instead of double-fetching under a divergent
// key that also misses SSE invalidation.
export const marketDetailQueryOptions = (api: ApiInstance, searchParams: MarketDetailParams) => ({
  queryKey: [undefined, 'MarketDetail', searchParams] as const,
  queryFn: async () => {
    const { data } = await api.market.getMarketDetail(searchParams);
    return applyResolvedPrices(data);
  },
});

export const useMarketDetail = (
  api: ApiInstance,
  searchParams: MarketDetailParams,
  options?: UseMarketDetailOptions
) => {
  const { marketAddress } = searchParams || {};
  const { queryKey, enabled = true, subscribe = true, ...rest } = options || {};

  return useSSEQuery(
    api.sse,
    {
      ...rest,
      enabled: enabled && hasNonEmptyProp(searchParams, ['marketAddress']),
      queryKey: [queryKey, 'MarketDetail', searchParams],
      queryFn: async () => {
        const { data } = await api.market.getMarketDetail(searchParams!);
        return applyResolvedPrices(data);
      },
    },
    subscribe
      ? [
          { event: 'market:price_change', topics: [marketAddress] },
          { event: 'market:info_update', topics: [marketAddress] },
        ]
      : []
  );
};
